import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import { EmailCheckView } from './email-check';
import { FederatedEntryView, FederatedRefusalView } from './federated-login';
import { MeView } from './me';
import { MfaSetupView } from './mfa-setup';
import { PasswordSetupView } from './setup-password';
import { SignInView } from './sign-in';
import { IdentityProviders } from 'korint-sdk/react';

/**
 * Get the auth routes.
 * @param logo - The logo to display in the auth pages.
 * @param providers - The auth providers to display in the auth pages.
 * @param showLastLoginHint - Whether to show the last login method hint.
 * @returns The auth routes.
 */
export function getAuthRoutes({
  logo,
  providers,
  showLastLoginHint = false,
  link,
}: {
  logo: ReactNode;
  providers: IdentityProviders[];
  showLastLoginHint?: boolean;
  link?: { label: string; to: string };
}) {
  return [
    {
      path: '/auth',
      element: (
        <EmailCheckView
          logo={logo}
          providers={providers}
          showLastLoginHint={showLastLoginHint}
          link={link}
        />
      ),
    },
    {
      path: '/auth/sign-in',
      element: (
        <SignInView logo={logo} providers={providers} showLastLoginHint={showLastLoginHint} />
      ),
    },
    {
      path: '/auth/setup-password',
      element: <PasswordSetupView logo={logo} />,
    },
    {
      path: '/auth/mfa-setup',
      element: <MfaSetupView logo={logo} />,
    },
    {
      path: '/auth/me',
      element: <MeView logo={logo} />,
    },
  ];
}

/** Auth routes of an app whose brokers only log in through an external identity provider. */
export function getFederatedAuthRoutes({ logo }: { logo: ReactNode }): RouteObject[] {
  return [
    { path: '/auth/federated', element: <FederatedEntryView /> },
    { path: '/auth/refused', element: <FederatedRefusalView logo={logo} /> },
    {
      path: '/auth/*',
      element: <Navigate to="/auth/refused?reason=sign_in_with_portal" replace />,
    },
    {
      path: '/auth',
      element: <Navigate to="/auth/refused?reason=sign_in_with_portal" replace />,
    },
  ];
}

/**
 * @deprecated Use `getAuthRoutes` instead.
 * It is deprecated because of the naming convention. It should not be a hook, it has no hooks inside.
 */
export const useAuthRoutes = ({
  logo,
  providers = [],
}: {
  logo: ReactNode;
  providers: IdentityProviders[];
}): RouteObject[] => {
  return getAuthRoutes({ logo, providers });
};
